import React from 'react';
import MaterialTab from '@mui/material/Tab';
import {styled} from '@mui/material/styles';
import type {TabProps as MaterialTabProps} from '@mui/material/Tab';

type FuelTabSize = 'small' | 'medium' | 'large';
type ModifiedTabProps = Omit<MaterialTabProps, 'disableFocusRipple' | 'centerRipple' | 'disableRipple' | 'disableTouchRipple' | 'focusRipple' | 'onFocusVisible' | 'TouchRippleProps' | 'textColor'>;

export interface TabProps extends ModifiedTabProps {
    /**
     * Size
     */
    size?: FuelTabSize;
    /**
     * @ignore
     */
    variant?: string;
}

const StyledTab = styled(MaterialTab)<TabProps>(({
    theme, size, variant
}) => ({
    minWidth: 'auto',
    color: theme.tokens.FuelColorGray600,
    opacity: 1,
    padding: `${theme.tokens.FuelSpacing1} ${theme.tokens.FuelSpacing4}`,
    minHeight: 'auto',
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    alignItems: 'center',
    position: 'relative',

    '&:after': {
        content: '""',
        display: 'block',
        height: '3px',
        width: '100%',
        position: 'absolute',
        borderTopLeftRadius: '1px',
        borderTopRightRadius: '1px',
        backgroundColor: 'transparent',
        bottom: 0,

        ...(variant === 'container' || variant === 'container-outlined') && {
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            bottom: undefined,
            top: 0
        }
    },

    '&.Mui-focusVisible': {
        boxShadow: `inset 0px 0px 0 2px ${theme.tokens.FuelColorFocused}`
    },

    '&:not(.Mui-selected):hover': {
        color: theme.tokens.FuelColorGray600,
        backgroundColor: theme.tokens.FuelColorGray50,
        boxShadow: `inset 0px -1px ${theme.tokens.FuelColorBorderLight}`,
        ...(variant === 'container' || variant === 'container-outlined') && {
            boxShadow: `inset 0px -1px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 1px 0px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 0px 1px 0 0px ${theme.tokens.FuelColorBorderLight}`
        }
    },

    ...size === 'small' && {
        fontSize: theme.tokens.FuelFontSizeSm,
        padding: `0.375rem ${theme.tokens.FuelSpacing4}`, // 0.375rem - 6px
        height: '2rem' // 32px
    },

    ...size === 'medium' && {
        fontSize: theme.tokens.FuelFontSizeMd,
        padding: `${theme.tokens.FuelSpacing2} ${theme.tokens.FuelSpacing4}`, // 0.5rem - 8px
        height: '2.5rem' // 40px
    },

    ...size === 'large' && {
        fontSize: theme.tokens.FuelFontSizeLg,
        padding: `${theme.tokens.FuelSpacing3} ${theme.tokens.FuelSpacing4}`, // 0.75rem - 12px
        height: '3rem' // 48px
    },

    ...(variant === 'container' || variant === 'container-outlined') && {
        boxShadow: `inset 0px -1px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 1px 0px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 0px 1px 0 0px ${theme.tokens.FuelColorBorderLight}`,
        backgroundColor: theme.tokens.FuelColorBackgroundWhite,
        '&:last-of-type': {
            boxShadow: `inset 0px -1px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 1px 0px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 0px 1px 0 0px ${theme.tokens.FuelColorBorderLight}, inset -1px 0px 0 0px ${theme.tokens.FuelColorBorderLight}`
        },
        '&:not(.Mui-selected):last-of-type:hover': {
            boxShadow: `inset 0px -1px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 0px 1px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 1px 0px 0 0px ${theme.tokens.FuelColorBorderLight}, inset -1px 0px 0 0px ${theme.tokens.FuelColorBorderLight}`
        },
        '&.Mui-selected + .MuiTab-root': {
            boxShadow: `inset 0px -1px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 0px 1px 0 0px ${theme.tokens.FuelColorBorderLight}`
        },
        '&.Mui-selected + .MuiTab-root:last-of-type': {
            boxShadow: `inset 0px -1px 0 0px ${theme.tokens.FuelColorBorderLight}, inset 0px 1px 0 0px ${theme.tokens.FuelColorBorderLight}, inset -1px 0px 0 0px ${theme.tokens.FuelColorBorderLight}`
        }
    },
    '&.Mui-selected': {
        color: theme.tokens.FuelColorBrandPrimary,

        '&:after': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultSelectedBar
        },

        ...(variant === 'container' || variant === 'container-outlined') && {
            boxShadow: `inset -1px 0px 0 0px ${theme.tokens.FuelColorBorderBase}, inset 1px 0px 0 0px ${theme.tokens.FuelColorBorderBase}`,
            zIndex: 2
        },
        '&.Mui-focusVisible': {
            boxShadow: `inset 0px 0px 0 2px ${theme.tokens.FuelColorFocused}`
        }
    }
}));

export const Tab = React.forwardRef<any, TabProps>(({
    children, variant, className, ...props
}, ref) => (
    <StyledTab
        label={children}
        ref={ref}
        variant={variant}
        {...props}
    >{children}</StyledTab>
));

Tab.displayName = 'Tab';
